import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Stack, List, ListItem, Typography, Divider, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { toggleProcedureCodeSetMatching } from "../../../../../../app/slices/VerificationModuleSlice";
import { evaluateCodeSetMatches, resetCodeSetMatches, setDisableCodeSetMatching } from "../../../../../../services/verifyService/model/verification/VerificationSlice";
import { ApplyCodeSets } from "./ApplyCodeSets";
import { ApptNote } from "../ApptNote";
import { CodeSetMatch } from "../../../../specs/codeSets/ProcedureCodeSetMatch";

function ProcedureCodeSetMatching() {
    const isOpen = useAppSelector(state => state.verificationModule.ProcedureCodeSetMatchingModal);
    const dispatch = useAppDispatch();
    const verification = useAppSelector(state => state.verification.verification);
    
    // State to track which code sets are selected
    const [selectedCodeSets, setSelectedCodeSets] = useState<{ [key: string]: boolean }>({});
    // State to track disable future matches checkbox
    const [disableFutureMatches, setDisableFutureMatches] = useState<boolean>(verification.disableCodeSetMatching || false);
    // State to track if procedures were just added (not on initial load)
    const [prevProcedureCount, setPrevProcedureCount] = useState<number>(0);
    // State to track if we're in the initial load
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    
    // Initialize selected state when matches change
    useEffect(() => {
        if (verification.codeSetMatches && verification.codeSetMatches.length > 0) {
            const initialSelections = verification.codeSetMatches.reduce((acc, match, index) => {
                acc[index.toString()] = false; // Start with none selected
                return acc;
            }, {} as { [key: string]: boolean });
            setSelectedCodeSets(initialSelections);
        }
    }, [verification.codeSetMatches]);
    
    // Handle the initial load case
    useEffect(() => {
        if (isInitialLoad) {
            // Record the initial procedure count
            setPrevProcedureCount(verification.procedures?.length || 0);
            setIsInitialLoad(false);
        }
    }, [isInitialLoad, verification.procedures]);
    
    // Run evaluation only when procedures are added (not on initial load)
    useEffect(() => {
        if (!isInitialLoad && verification.procedures) {
            const currentCount = verification.procedures.length;
            
            // Only evaluate if a procedure was added (current count > previous count)
            if (currentCount > prevProcedureCount && currentCount > 0) {
                dispatch(evaluateCodeSetMatches());
            }
            
            // Update the previous count
            setPrevProcedureCount(currentCount);
        }
    }, [verification.procedures, prevProcedureCount, isInitialLoad, dispatch]);
    
    // Open the modal if we have any matches
    useEffect(() => {
        if (verification.codeSetMatches && verification.codeSetMatches.length > 0 && !isOpen) {
            dispatch(toggleProcedureCodeSetMatching());
        }
    }, [verification.codeSetMatches, isOpen, dispatch]);
    
    // Handle checkbox change
    const handleCheckboxChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCodeSets(prev => ({
            ...prev,
            [index.toString()]: event.target.checked
        }));
    };
    
    // Get selected code sets
    const getSelectedCodeSets = (): CodeSetMatch[] => {
        if (!verification.codeSetMatches) return [];
        
        return verification.codeSetMatches.filter((_, index) => 
            selectedCodeSets[index.toString()]
        );
    };
    
    // Handle disable future matches checkbox change
    const handleDisableFutureMatchesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDisableFutureMatches(event.target.checked);
    };
    
    const close = () => {
        // Update the disable future matches setting in the store
        dispatch(setDisableCodeSetMatching(disableFutureMatches));
        dispatch(toggleProcedureCodeSetMatching());
        dispatch(resetCodeSetMatches());
        setSelectedCodeSets({});
    };
    
    return (
        <React.Fragment>
            <Dialog 
                open={isOpen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Procedure Code Set Match</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <ApptNote />
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        We've detected that you're working with procedures that are part of common code sets. 
                        Please select which code sets you'd like to add:
                    </Typography>
                    <FormGroup sx={{ mt: 1 }}>
                        {verification.codeSetMatches?.map((match, index) => (
                            <React.Fragment key={index}>
                                <Divider sx={{ my: 1 }} />
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={selectedCodeSets[index.toString()] || false}
                                            onChange={handleCheckboxChange(index)}
                                            name={`codeSet-${index}`}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {match.name}
                                        </Typography>
                                    }
                                />
                                <Typography variant="caption" sx={{ ml: 4, display: 'block', mb: 0.5 }}>
                                    <strong>Matched:</strong> {match.matchedOn.join(", ")}
                                </Typography>
                                <Typography variant="caption" sx={{ ml: 4, display: 'block' }}>
                                    <strong>Add:</strong> {match.cptCodes.join(", ")}
                                </Typography>
                            </React.Fragment>
                        ))}
                        {/* Disable future matches checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={disableFutureMatches}
                                    onChange={handleDisableFutureMatchesChange}
                                    name="disableFutureMatches"
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="caption">
                                    Don't show code sets for this estimate
                                </Typography>
                            }
                            sx={{ mt: 2 }}
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button variant='outlined' size="small" onClick={() => close()}>Cancel</Button>
                    <ApplyCodeSets selectedCodeSets={getSelectedCodeSets()} />
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export { ProcedureCodeSetMatching };
